/*
 * The site's own words in Danish (S48, 2026-10-29) - line for line the English
 * of en.ts, which is the contract: a line missing here, or one here that English
 * no longer has, fails the typecheck.
 *
 * Dates are not here: the language's locale writes them (language.ts), month
 * names included - "28. oktober 2026".
 *
 * ERRORS. The server writes its messages in English, and an error banner shows
 * them. The ones a reader can meet on the public pages are mapped to Danish
 * below, by their exact wording - dictionaries.test.ts checks that each still appears in
 * the server's source, so a reworded message is caught rather than left English
 * by a map that quietly stopped matching. Anything not in the map is shown in
 * English as it came, which beats a Danish line that says less.
 */

import { attachmentFileTypeForName, formatByteSize } from '@accotool-kb/shared';
import type { Messages } from './en.js';
import { rulesDa } from './admin/rules.da.js';
import { shellDa } from './admin/shell.da.js';
import { articlesDa } from './admin/articles.da.js';
import { editorDa } from './admin/editor.da.js';
import { releaseNotesDa } from './admin/releaseNotes.da.js';
import { siteDa } from './admin/site.da.js';

/** An attachment's type, by extension - every type in ATTACHMENT_FILE_TYPES. */
const FILE_TYPES: Record<string, string> = {
  pdf: 'PDF',
  docx: 'Word-dokument',
  xlsx: 'Excel-projektmappe',
  pptx: 'PowerPoint-præsentation',
  txt: 'tekstfil',
  csv: 'CSV-fil',
  json: 'JSON-fil',
  xml: 'XML-fil',
  zip: 'ZIP-arkiv',
  pbix: 'Power BI-rapport',
  pbit: 'Power BI-skabelon',
  png: 'PNG-billede',
  jpg: 'JPEG-billede',
  jpeg: 'JPEG-billede',
  gif: 'GIF-billede',
  webp: 'WEBP-billede',
};

/** The server's and the API client's fixed messages that a reader can meet. */
export const DANISH_ERROR_MESSAGES: Record<string, string> = {
  'Something went wrong.': 'Noget gik galt.',
  'That article is not available.': 'Artiklen er ikke tilgængelig.',
  'That release note does not exist.': 'Udgivelsesnoten findes ikke.',
  'Not found.': 'Ikke fundet.',
  'Sign-in is not configured on this server.': 'Login er ikke konfigureret på denne server.',
  'Some values were rejected.': 'Nogle værdier blev afvist.',
};

export const da: Messages = {
  common: {
    loadingLabel: 'Indlæser',
    loading: 'Indlæser…',
    somethingWentWrong: 'Noget gik galt.',
    tryAgain: 'Prøv igen',
    dismiss: 'Luk',
    close: 'Luk',
    concurrencyConflict: 'Genindlæs siden for at hente den anden ændring, og foretag så din egen igen.',
    skipToContent: 'Spring til indholdet',
    breadcrumb: 'Brødkrummesti',
    onThisPage: 'På denne side',
    publishedOn: 'Udgivet {date}',
    nothingToFilterBy: 'Der er endnu intet at filtrere efter.',
  },

  nav: {
    label: 'Websted',
    articles: 'Artikler',
    releaseNotes: 'Udgivelsesnoter',
    manage: 'Administrer',
    signIn: 'Log ind',
    signingIn: 'Logger ind…',
    signOut: 'Log ud',
  },

  appearance: {
    label: 'Udseende',
    light: 'Lys',
    dark: 'Mørk',
  },

  language: {
    label: 'Sprog',
    current: (name) => `Sprog: ${name}`,
  },

  layout: {
    loadingKnowledgeBase: 'Indlæser vidensbasen…',
  },

  filters: {
    heading: 'Filtre',
    searchPlaceholder: 'Søg…',
    product: 'Produkt',
    productRule: 'Matcher ethvert af de valgte produkter.',
    category: 'Kategori',
    categoryRule: 'Matcher enhver af de valgte kategorier.',
    combinationRule:
      'En artikel vises, når den matcher **mindst én** af de værdier, der er sat flueben ved i en liste, og den skal matche **alle** de lister, du har sat flueben i.',
    clear: (count) => `Ryd ${count} ${count === 1 ? 'filter' : 'filtre'}`,
  },

  pagination: {
    label: 'Sideinddeling',
    previous: 'Forrige',
    next: 'Næste',
    range: (first, last, total) => `${first}–${last} af ${total}`,
  },

  browse: {
    searchLabel: 'Søg i artikler',
    articles: 'Artikler',
    promoted: 'Fremhævede',
    // The owner's words, capital S included (S51).
    questions: 'Hyppige Spørgsmål',
    count: (total, grouped) =>
      `${total} ${total === 1 ? 'artikel' : 'artikler'}${grouped ? ', grupperet efter produkt' : ''}`,
    sortBy: 'Sortér efter',
    sortBestMatch: 'Bedste match',
    sortMostRecent: 'Nyeste',
    sortTitle: 'Titel',
    finding: 'Finder artikler…',
    nothingMatches: 'Ingen resultater',
    tryBroader: 'Prøv at fjerne et filter eller søge bredere.',
    noneYet: 'Der er endnu ikke udgivet nogen artikler.',
    groupsReflectPage: 'Grupperne gælder kun resultaterne på denne side.',
  },

  article: {
    loading: 'Indlæser artiklen…',
    back: 'Tilbage til vidensbasen',
    readingTime: (minutes) => `${minutes} min. læsetid`,
    revision: (number) => `Revision ${number}`,
    related: 'Relaterede artikler',
    attachments: 'Vedhæftede filer',
    download: 'Hent',
    downloadFile: (fileName) => `Hent ${fileName}`,
    fileType: (fileName, contentType) => {
      const type = attachmentFileTypeForName(fileName);
      return type ? (FILE_TYPES[type.extension] ?? type.label) : contentType;
    },
    // Decimal comma, and "byte" has no plural in Danish.
    byteSize: (bytes) => formatByteSize(bytes).replace('.', ',').replace(/ bytes$/, ' byte'),
    edit: 'Rediger artiklen',
  },

  audience: {
    privateFor: (name) => `Privat: kun ${name} kan læse denne artikel`,
    privateForNobody: 'Privat: ikke for alle',
    hiddenPrivateFor: 'Privat, for ',
    hiddenPrivate: 'Privat',
  },

  releaseNotes: {
    searchLabel: 'Søg i udgivelsesnoter',
    heading: 'Udgivelsesnoter',
    count: (total, search) => {
      const notes = `${total} ${total === 1 ? 'udgivelsesnote' : 'udgivelsesnoter'}`;
      return search ? `${notes} matcher »${search}«` : notes;
    },
    finding: 'Finder udgivelsesnoter…',
    nothingOnThisPage: 'Intet på denne side',
    nothingMatches: 'Ingen resultater',
    fewerPages: 'Listen har færre sider, end denne adresse beder om.',
    firstPage: 'Gå til første side',
    noMatch: (search, inSelectedProducts) =>
      `Ingen udgivelsesnoter matcher »${search}«${
        inSelectedProducts ? ' i de valgte produkter' : ''
      }. Prøv at fjerne et filter eller søge bredere.`,
    noneForProducts: 'Ingen udgivelsesnoter hører til de valgte produkter. Prøv at fjerne et filter.',
    noneYet: 'Der er endnu ikke udgivet nogen udgivelsesnoter.',
    loading: 'Indlæser udgivelsesnoten…',
    back: 'Tilbage til udgivelsesnoterne',
  },

  account: {
    signedInAs: 'Logget ind som',
    unnamedAccount: 'En unavngiven konto',
    accessVia: (how) => `Adgang via ${how}`,
    grantedVia: {
      'app-role': 'approlle',
      'groups-claim': 'gruppekrav',
      'microsoft-graph': 'Microsoft Graph',
    },
    signInHint: 'Nogle artikler er kun tilgængelige for læsere, der er logget ind.',
  },

  notFound: {
    title: 'Siden findes ikke',
    body: 'Linket kan være forældet, eller siden kan være blevet afpubliceret eller flyttet.',
    home: 'Gå til vidensbasen',
  },

  markdown: {
    videoNotEmbeddable: 'Videoen kan ikke indlejres.',
    imageNotAvailable: 'Billedet er ikke tilgængeligt',
    imageNotAvailableAlt: (alt) => `Billedet er ikke tilgængeligt: ${alt}`,
  },

  footer: {
    columns: {
      product: 'Produkt',
      resources: 'Ressourcer',
      contact: 'Kontakt',
    },
    linkedIn: (company) => `${company} på LinkedIn`,
  },

  adminGate: {
    preparingSignIn: 'Forbereder login…',
    configErrorHelp: 'Det offentlige websted er ikke berørt. Tjek serverens log og {ready}.',
    sessionExpired: 'Din session er udløbet. Du sendes tilbage til login…',
    checkingAccess: 'Kontrollerer din adgang…',
    slowTitle: 'Det tager længere tid, end det burde',
    slowIntro:
      'Dit login lykkedes, men serveren har endnu ikke bekræftet din adgang. Kontrollen er en enkelt forespørgsel og svarer normalt med det samme.',
    slowLookAt: 'Værd at undersøge, i denne rækkefølge:',
    slowNetwork:
      'Browserens netværksfane. Et *gentaget* kald til {me} betyder, at siden kører i ring; ét enkelt ventende kald betyder, at serveren ikke svarer.',
    slowHealth: '{ready} — bekræfter, at serveren kører, og viser, om Entra ID-indstillingerne er komplette.',
    slowConsole: 'Browserens konsol, for en MSAL-fejl under hentningen af adgangstokenet.',
    reload: 'Genindlæs',
    signInTitle: 'Log ind for at administrere vidensbasen',
    signInBody:
      'Administration af artikler og metadata kræver en arbejdskonto, som din organisation har givet adgang til at administrere denne vidensbase.',
    signInWithMicrosoft: 'Log ind med Microsoft',
    readingNeedsNoSignIn: 'Du behøver ikke at logge ind for at læse i vidensbasen.',
    notConfiguredTitle: 'Login er endnu ikke konfigureret',
    notConfiguredBody:
      'Serveren har ikke fuldstændige Entra ID-indstillinger, så administrationen kan ikke godkende nogen. Den offentlige vidensbase er ikke berørt.',
    notConfiguredFix:
      'Angiv {tenant}, {api}, {spa} og {group} i App Service-programindstillingerne. {ready} viser præcis, hvilke der mangler, og {doc} gennemgår app-registreringen.',
    noAccessTitle: 'Du har ikke adgang',
    noAccessNotMember:
      'Dit login lykkedes, men du er ikke medlem af den gruppe, der må administrere denne vidensbase.',
    noAccessAsk:
      'Bed en administrator i din organisation om at give din konto adgang til at administrere denne vidensbase. Ændringer i adgangen træder i kraft, næste gang du logger ind, fordi det er dit adgangstoken, der bærer dem.',
    checkAgain: 'Kontrollér igen',
    signInAsSomeoneElse: 'Log ind som en anden',
  },

  errors: {
    describe: (code, message) => {
      // The API client's own words for a request that never reached the server.
      if (code === 'network-error') return 'Vidensbasen kunne ikke nås. Tjek din forbindelse, og prøv igen.';

      const known = DANISH_ERROR_MESSAGES[message];
      if (known) return known;

      // A server fault ends with the reference its log line carries; keep it.
      const fault = /^Something went wrong\. Reference (\S+)\.$/.exec(message);
      if (fault) return `Noget gik galt. Reference ${fault[1]}.`;

      const failed = /^The request failed \((\d+)\)\.$/.exec(message);
      if (failed) return `Forespørgslen mislykkedes (${failed[1]}).`;

      const image = /^The image could not be loaded \((\d+)\)\.$/.exec(message);
      if (image) return `Billedet kunne ikke indlæses (${image[1]}).`;

      return message;
    },
  },

  admin: {
    rules: rulesDa,
    shell: shellDa,
    articles: articlesDa,
    editor: editorDa,
    releaseNotes: releaseNotesDa,
    site: siteDa,
  },
};
